/** Casas decimais do real. */
const CENTS = 100;

/**
 * Formata um número como moeda para exibição **dentro de um input**.
 *
 * Diferente de `formatCurrency`, que é para leitura: aqui um valor ausente vira
 * string vazia, e não um travessão — um placeholder textual dentro de um campo
 * editável seria interpretado como conteúdo digitável.
 *
 * @param value - Valor em reais.
 * @returns Texto no formato `R$ 1.234,56`, ou `''` quando não há valor.
 *
 * @example
 * ```ts
 * formatCurrencyInput(24000); // 'R$ 24.000,00'
 * formatCurrencyInput(null);  // ''
 * ```
 */
export function formatCurrencyInput(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

/**
 * Interpreta o texto digitado num campo de moeda.
 *
 * Lê **apenas os dígitos** e trata os dois últimos como centavos, que é como
 * máscara de moeda se comporta: digitar `2`, `4`, `0`, `0` produz `R$ 24,00`. A
 * alternativa — máscara de padrão fixo do ADS (`mask="R$ 999,99"`) — não serve
 * porque o valor tem comprimento variável e o padrão obrigaria sempre a mesma
 * quantidade de dígitos.
 *
 * @param raw - Conteúdo atual do input.
 * @returns Valor em reais, ou `null` quando não há dígito algum.
 *
 * @example
 * ```ts
 * parseCurrencyInput('R$ 24.000,00'); // 24000
 * parseCurrencyInput('2400');         // 24
 * parseCurrencyInput('');             // null
 * ```
 */
export function parseCurrencyInput(raw: string): number | null {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return null;
  return Number(digits) / CENTS;
}

/**
 * Formata um número como percentual para exibição **dentro de um input**.
 *
 * @param value - Percentual (`15` para 15%).
 * @returns Texto no formato `15%` ou `12,5%`, ou `''` quando não há valor.
 *
 * @example
 * ```ts
 * formatPercentageInput(15);   // '15%'
 * formatPercentageInput(12.5); // '12,5%'
 * formatPercentageInput(null); // ''
 * ```
 */
export function formatPercentageInput(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value)}%`;
}

/**
 * Interpreta o texto digitado num campo de percentual.
 *
 * Ao contrário da moeda, os dígitos são lidos **de frente**, não como centavos:
 * digitar `1` e `5` produz `15%`, não `0,15%`. Percentual é escrito como se lê,
 * e tratar os dois últimos dígitos como decimais tornaria impossível digitar um
 * inteiro sem passar por valores absurdos.
 *
 * Aceita vírgula e ponto como separador decimal, limitado a duas casas.
 *
 * @param raw - Conteúdo atual do input.
 * @returns Percentual, ou `null` quando não há dígito algum.
 *
 * @example
 * ```ts
 * parsePercentageInput('15%');   // 15
 * parsePercentageInput('12,5%'); // 12.5
 * parsePercentageInput('');      // null
 * ```
 */
export function parsePercentageInput(raw: string): number | null {
  const cleaned = raw.replace(/[^\d,.]/g, '').replace(',', '.');
  // Mais de um separador vem de digitação intermediária; fica com o primeiro.
  const [whole, ...rest] = cleaned.split('.');
  const normalized = rest.length > 0 ? `${whole}.${rest.join('').slice(0, 2)}` : whole;

  if (normalized === undefined || normalized === '' || normalized === '.') return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}
